import posthog from 'posthog-js';

// ─── Init ─────────────────────────────────────────────────────────────────────
// No-ops silently when VITE_POSTHOG_KEY is absent (local dev without a key).

export function initPostHog() {
  const key  = import.meta.env.VITE_POSTHOG_KEY  as string | undefined;
  const host = import.meta.env.VITE_POSTHOG_HOST as string | undefined;
  if (!key) return;

  posthog.init(key, {
    api_host:              host ?? 'https://us.i.posthog.com',
    capture_pageview:      false, // we fire page views via the router integration below
    capture_pageleave:     true,
    persistence:           'localStorage',
    autocapture:           false, // explicit track() calls only — avoids noisy clicks
    session_recording:     { maskAllInputs: true },
  });
}

// ─── Identity ─────────────────────────────────────────────────────────────────

export function identifyUser(id: string, traits: {
  name:         string;
  email?:       string;
  industry?:    string | null;
  status?:      string;
  verification?: string;
}) {
  if (!posthog.__loaded) return;
  posthog.identify(id, {
    name:         traits.name,
    email:        traits.email,
    industry:     traits.industry,
    status:       traits.status,
    verification: traits.verification,
  });
}

export function resetUser() {
  if (!posthog.__loaded) return;
  posthog.reset();
}

export function capturePageView(path: string) {
  if (!posthog.__loaded) return;
  posthog.capture('$pageview', { $current_url: window.location.origin + path });
}

// ─── Typed event catalogue ────────────────────────────────────────────────────

export type TrackEvent =
  | { event: 'signed_up';                    props?: { industry?: string } }
  | { event: 'signed_in' }
  | { event: 'signed_out' }
  | { event: 'profile_claimed';              props: { handle: string } }
  | { event: 'profile_updated' }
  | { event: 'post_created';                 props: { visibility: string } }
  | { event: 'connection_sent' }
  | { event: 'connection_accepted' }
  | { event: 'event_rsvp';                   props: { status: string } }
  | { event: 'job_posted' }
  | { event: 'venture_created';              props: { kind: string } }
  | { event: 'investor_interest_expressed' }
  | { event: 'mentorship_opted_in';          props: { role: string } }
  | { event: 'mentorship_match_requested' }
  | { event: 'group_joined' }
  | { event: 'group_created' }
  | { event: 'search_performed';             props: { query_length: number; result_count: number } }
  ;

export function track(args: TrackEvent) {
  if (!posthog.__loaded) return;
  const { event, ...rest } = args as { event: string; props?: Record<string, unknown> };
  posthog.capture(event, rest.props);
}
